import React, { createContext, useState, useEffect, useCallback, useContext } from "react";
import { auth } from '../Config/firebase';
import { onAuthStateChanged, signOut } from "firebase/auth";

export const AuthContext = createContext({});

function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [appUserData, setAppUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loginError, setLoginError] = useState(null); // NOVO: Estado para erro de login

    const fetchUserContext = async (user, force = false) => {
        try {
            const token = await user.getIdToken();
            const API_URL = process.env.REACT_APP_API_URL;
            // Adiciona ?force=true se necessário
            const url = `${API_URL}/api/user-context${force ? '?force=true' : ''}`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-session-id': localStorage.getItem('sessionId')
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                const err = new Error(errorData.error || 'Falha ao autenticar com o servidor.');
                if (errorData.code) err.code = errorData.code; // Attach code (SESSION_CONFLICT)
                throw err;
            }

            const contextData = await response.json();


            setCurrentUser(user);
            setAppUserData(contextData);
            setLoginError(null); // Limpa erros anteriores

            // --- CORREÇÃO: SALVAR SESSION ID ---
            if (contextData.sessionId) {
                localStorage.setItem('sessionId', contextData.sessionId);
                console.log('[AUTH] SessionID atualizado:', contextData.sessionId);
            }
            // -----------------------------------

        } catch (error) {
            console.error("Falha ao buscar contexto do usuário.", error);

            if (error.code === 'SESSION_CONFLICT') {
                // NÃO Desloga ainda. Apenas define o erro para a UI tratar (perguntar ao usuário)
                setLoginError({ message: error.message, code: error.code }); // Passa objeto
                // Mantém currentUser null na appUserData por enquanto para travar navegação?
                // Na verdade, onAuthStateChanged já setou currentUser (firebase user).
                // Mas appUserData é null, então PrivateRoute (se fosse usado direto) barraria.
                // Mas aqui estamos no Login page provavelmente.
            } else {
                // Erro fatal (403, 500 etc) -> Desloga
                if (error.code === 'SUBSCRIPTION_EXPIRED') {
                    setLoginError({ message: error.message, code: error.code });
                } else if (error.message && error.message !== 'Falha ao autenticar com o servidor.') {
                    setLoginError(error.message);
                } else {
                    setLoginError("Erro ao conectar com o servidor. Tente novamente.");
                }

                localStorage.removeItem('sessionId');
                await signOut(auth);
                setCurrentUser(null);
                setAppUserData(null);
            }
        }
    };

    const retrySession = (force) => {
        if (auth.currentUser) {
            setLoading(true);
            fetchUserContext(auth.currentUser, force).finally(() => setLoading(false));
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            const localSession = localStorage.getItem('sessionId');
            const isLoggingIn = sessionStorage.getItem('isLoggingIn');

            console.log('[AUTH DEBUG] onAuthStateChanged User:', user ? user.uid : 'null', 'Session:', localSession, 'LoggingIn:', isLoggingIn);

            if (user) {
                if (localSession || isLoggingIn) {
                    await fetchUserContext(user);
                    if (isLoggingIn) sessionStorage.removeItem('isLoggingIn');
                } else {
                    // Sessão inválida/inexistente + Não é login ativo -> Forçar Logout
                    console.warn("[AUTH DEBUG] Sessão não detectada e não é login explícito. Deslogando.");
                    await signOut(auth);
                    setCurrentUser(null);
                    setAppUserData(null);
                }
            } else {
                setCurrentUser(null);
                setAppUserData(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const signOutUser = useCallback(async () => {
        try {
            // Tentar avisar o backend para destruir a sessão
            const API_URL = process.env.REACT_APP_API_URL;
            const sessionId = localStorage.getItem('sessionId');
            if (sessionId && API_URL) {
                // Dispara o logout sem esperar muito (fire and forget)
                fetch(`${API_URL}/api/logout`, {
                    method: 'POST',
                    headers: { 'x-session-id': sessionId }
                }).catch(err => console.error("Erro no logout backend:", err));
            }
        } catch (e) {
            console.error(e);
        } finally {
            localStorage.removeItem('sessionId');
            sessionStorage.removeItem('auth_conflict_pending');
            try {
                await signOut(auth);
            } catch (error) {
                console.error("Erro ao fazer logout firebase:", error);
            }
        }
    }, []);

    const authContextValue = {
        currentUser,
        appUserData,
        tenantId: appUserData?.tenant_id,
        logado: !!currentUser,
        loading,
        loginError, // Expondo o erro
        setLoginError, // Expondo setter caso precise limpar
        signOutUser,
        retrySession, // Expondo retry
        fetchUserContext // Expondo para chamada manual no Login
    };

    return (
        <AuthContext.Provider value={authContextValue}>
            {!loading && children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    return useContext(AuthContext);
};

export default AuthProvider;